// @ts-nocheck
// ============================================================
// CAMERA MANAGER — Fase 9.2
// CRUD, status, registro de câmeras, stream management
// ============================================================
import { supabase } from '@/lib/supabase'
import type { CameraDevice, CameraZone, CameraStatus } from '@/types/vision'
import { obs } from '@/core/observability'

// ─── CRUD ────────────────────────────────────────────────────
export async function getCameras(obraId: string): Promise<CameraDevice[]> {
  const { data, error } = await supabase
    .from('camera_devices')
    .select('*')
    .eq('obra_id', obraId)
    .order('nome')

  if (error) { obs.warn('camera_manager', error.message); return [] }
  return (data ?? []) as CameraDevice[]
}

export async function createCamera(
  payload: Omit<CameraDevice, 'id' | 'created_at' | 'updated_at' | 'last_heartbeat'>,
): Promise<CameraDevice | null> {
  const { data, error } = await supabase
    .from('camera_devices')
    .insert(payload)
    .select()
    .single()

  if (error) { obs.error('camera_manager', `Create failed: ${error.message}`); return null }
  return data as CameraDevice
}

export async function updateCamera(
  id: string,
  patch: Partial<CameraDevice>,
): Promise<boolean> {
  const { error } = await supabase
    .from('camera_devices')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) { obs.error('camera_manager', `Update failed: ${error.message}`); return false }
  return true
}

export async function deleteCamera(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('camera_devices')
    .delete()
    .eq('id', id)

  if (error) { obs.error('camera_manager', `Delete failed: ${error.message}`); return false }
  return true
}

// ─── Heartbeat ────────────────────────────────────────────────
export async function sendHeartbeat(cameraId: string): Promise<void> {
  await supabase
    .from('camera_devices')
    .update({ last_heartbeat: new Date().toISOString(), status: 'online' })
    .eq('id', cameraId)
}

// ─── Zone management ─────────────────────────────────────────
export async function getZones(cameraId: string): Promise<CameraZone[]> {
  const { data, error } = await supabase
    .from('camera_zones')
    .select('*')
    .eq('camera_id', cameraId)
    .eq('is_active', true)

  if (error) { obs.warn('camera_manager', error.message); return [] }
  return (data ?? []) as CameraZone[]
}

export async function createZone(
  payload: Omit<CameraZone, 'id' | 'created_at'>,
): Promise<CameraZone | null> {
  const { data, error } = await supabase
    .from('camera_zones')
    .insert(payload)
    .select()
    .single()

  if (error) { obs.error('camera_manager', `Zone create failed: ${error.message}`); return null }
  return data as CameraZone
}

// ─── Status helpers ───────────────────────────────────────────
export function getStatusColor(status: CameraStatus): string {
  switch (status) {
    case 'online':  return '#34d399'
    case 'offline': return '#6b7280'
    case 'error':   return '#ef4444'
    case 'standby': return '#fbbf24'
    default:        return '#6b7280'
  }
}

export function getStatusLabel(status: CameraStatus): string {
  switch (status) {
    case 'online':  return 'Online'
    case 'offline': return 'Offline'
    case 'error':   return 'Erro'
    case 'standby': return 'Standby'
    default:        return 'Desconhecido'
  }
}

// ─── Stream URL resolver ───────────────────────────────────────
export function resolveStreamUrl(camera: CameraDevice): string | null {
  // WebRTC peer ID takes precedence
  if (camera.webrtc_peer) return `webrtc://${camera.webrtc_peer}`
  // RTSP via backend proxy
  if (camera.rtsp_url) return `/api/stream-proxy?url=${encodeURIComponent(camera.rtsp_url)}&id=${camera.id}`
  // Static snapshot fallback
  if (camera.snapshot_url) return camera.snapshot_url
  return null
}

// ─── Snapshot URL for latest ──────────────────────────────────
export async function getLatestSnapshotUrl(cameraId: string): Promise<string | null> {
  const { data } = await supabase
    .from('camera_snapshots')
    .select('storage_path, storage_bucket')
    .eq('camera_id', cameraId)
    .order('taken_at', { ascending: false })
    .limit(1)
    .single()

  if (!data) return null

  const { data: signedData } = supabase.storage
    .from(data.storage_bucket)
    .getPublicUrl(data.storage_path)

  return signedData?.publicUrl ?? null
}
